import { forwardRef } from "react";
import { CustomTellescopeFormFieldProps } from "../../types";
import { Input } from "@tellescope/design-system";
import { Button, cn, Textarea } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { BelugaPatientPreferenceMedicationValue } from "./types";

export interface BelugaPatientPreferenceMedicationProps
  extends CustomTellescopeFormFieldProps<BelugaPatientPreferenceMedicationValue> {
  onRemove: () => void;
  onBlur?: () => void;
}

export const BelugaPatientPreferenceMedication = forwardRef<HTMLDivElement, BelugaPatientPreferenceMedicationProps>(
  ({ value, onChange, onRemove, className, onBlur, ...rest }, ref) => {
    const { t } = useTranslation("forms");

    const update = (key: keyof BelugaPatientPreferenceMedicationValue, newValue: string) => {
      onChange({ ...value, [key]: newValue });
    };

    return (
      <div className={cn("space-y-2 pb-4 mb-2 border-b", className)} {...rest} ref={ref}>
        <Input
          value={value.name}
          onChange={(e) => update("name", e.target.value)}
          onBlur={onBlur}
          required
          placeholder={t("Medication Name")}
          className="w-full"
        />
        <div className="flex gap-2 w-full">
          <Input
            value={value.strength}
            onChange={(e) => update("strength", e.target.value)}
            onBlur={onBlur}
            required
            placeholder={t("Strength")}
          />
          <Input
            value={value.dispenseUnit}
            onChange={(e) => update("dispenseUnit", e.target.value)}
            onBlur={onBlur}
            placeholder={t("Dispense Unit")}
          />
        </div>
        <div className="flex gap-2 w-full">
          <Input
            value={value.quantity}
            onChange={(e) => update("quantity", e.target.value)}
            onBlur={onBlur}
            placeholder={t("Quantity")}
          />
          <Input
            value={value.refills}
            onChange={(e) => update("refills", e.target.value)}
            onBlur={onBlur}
            placeholder={t("Refills")}
          />
          <Input
            value={value.daysSupply}
            onChange={(e) => update("daysSupply", e.target.value)}
            onBlur={onBlur}
            placeholder={t("Days Supply")}
          />
        </div>
        <Textarea
          value={value.sig}
          onChange={(e) => update("sig", e.target.value)}
          onBlur={onBlur}
          placeholder={t("Sig (Instructions)")}
          className="w-full"
        />
        <Input
          value={value.medId}
          onChange={(e) => update("medId", e.target.value)}
          onBlur={onBlur}
          placeholder={t("Med ID (NDC11)")}
          className="w-full"
        />
        <div className="flex justify-end">
          <Button variant="outline" onClick={onRemove}>
            {t("Remove Medication")}
          </Button>
        </div>
      </div>
    );
  }
);
